import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { customMapStyle, routes } from "../utils/symbols";
import {
	orderCentersByDistance,
	getAvailableAppointments,
	extractTimeData,
} from "../utils/helpers.js";
import { t } from "../i18n";
import { Icon } from "../components/Icon";
import { AvailableAppointments } from "../components/AvailableAppointments";

const containerStyle = {
	width: "100%",
	height: "100%",
};

const GOOGLE_LIBRARIES = ["places"];

let RALARSA_URL = process.env.REACT_APP_RALARSA_URL;

export const CenterMap = ({
	selectedCenter,
	setSelectedCenter,
	filterCentersByDistance,
	selectCenter,
	mapCenter,
	setMapCenter,
	defaultCenter,
	mapRef,
	setSearchQuery,
	setMapIsLoaded,
	onMapChange,
}) => {
	const {
		centers,
		filteredCenters,
		setFilteredCenters,
		visibleCenters,
		setVisibleCenters,
		setGeolocationFailed,
		bookingSent,
		data,
		setData,
		searchQuery,
		viewMap,
		navigate,
	} = useContext(AppContext);
	const [infoCenter, setInfoCenter] = useState(null);
	const [userLocation, setUserLocation] = useState(null);
	const [googleDetails, setGoogleDetails] = useState(null);
	const [zoomLevel, setZoomLevel] = useState(6);
	const [infoCenterAppointments, setInfoCenterAppointments] = useState(null);
	const [infoCenterAppointmentsLoading, setInfoCenterAppointmentsLoading] =
		useState(false);

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: "AIzaSyBCXJUUwJdWyFevGY5SoNhtqUVqUkS0Du0",
		libraries: GOOGLE_LIBRARIES,
	});

	const handleMapIdle = () => {
		if (!mapRef.current || !centers?.length) return;

		const bounds = mapRef.current.getBounds();
		if (!bounds) return;

		const visible = orderCentersByDistance(centers, mapCenter).filter(
			(center) => {
				const pos = new window.google.maps.LatLng(
					center.latitude,
					center.longitude,
				);
				return bounds.contains(pos);
			},
		);

		if (visible.length > 0) {
			setVisibleCenters(visible);
			if (onMapChange) onMapChange();
		}
	};

	const isSaturdayByAppointment =
		infoCenter?.saturdayFrom === "CITA PREVIA" &&
		infoCenter?.saturdayTo === "CITA PREVIA";

	const showSaturday =
		infoCenter?.saturdayFrom &&
		infoCenter?.saturdayTo &&
		(infoCenter?.saturdayFrom !== infoCenter?.saturdayTo ||
			isSaturdayByAppointment);

	const handleMarkerClick = (center) => {
		setSelectedCenter(center);
		if (!center.temporarilyClosed) {
			setData((prev) => ({
				...prev,
				centerId: center.id,
				centerName: center.name,
				centerCode: center.code,
				centerAddress: center.address,
			}));
		} else {
			setData((prev) => ({
				...prev,
				centerId: null,
				centerName: null,
				centerCode: null,
				centerAddress: null,
			}));
		}
		setMapCenter({
			lat: center.latitude,
			lng: center.longitude,
		});
		setInfoCenter(center);

		if (mapRef.current && center.latitude && center.longitude) {
			const currentZoom = mapRef.current.getZoom();
			mapRef.current.panTo({
				lat: center.latitude,
				lng: center.longitude,
			});
			if (currentZoom < 12) {
				mapRef.current.setZoom(12);
			}
		}

		const placesService = new window.google.maps.places.PlacesService(
			mapRef.current,
		);

		const placeSearchRequest = {
			query: center.name,
			location: { lat: center.latitude, lng: center.longitude },
			radius: 1000,
		};

		placesService.textSearch(placeSearchRequest, (results, status) => {
			if (
				status === window.google.maps.places.PlacesServiceStatus.OK &&
				results[0]
			) {
				const placeId = results[0].place_id;

				placesService.getDetails(
					{
						placeId,
						fields: ["rating", "user_ratings_total"],
					},
					(placeResult, detailStatus) => {
						if (
							detailStatus ===
							window.google.maps.places.PlacesServiceStatus.OK
						) {
							setGoogleDetails((prev) => ({
								...prev,
								rating: placeResult.rating,
								totalReviews: placeResult.user_ratings_total,
							}));
						}
					},
				);
			}
		});

		if (userLocation) {
			const distanceService =
				new window.google.maps.DistanceMatrixService();

			distanceService.getDistanceMatrix(
				{
					origins: [userLocation],
					destinations: [
						{ lat: center.latitude, lng: center.longitude },
					],
					travelMode: window.google.maps.TravelMode.DRIVING,
					unitSystem: window.google.maps.UnitSystem.METRIC,
				},
				(response, status) => {
					if (status === "OK") {
						const element = response.rows[0].elements[0];
						if (element.status === "OK") {
							const distanceText = element.distance.text;

							setGoogleDetails((prev) => ({
								...prev,
								distance: distanceText,
							}));
						}
					} else {
						console.warn("DistanceMatrix error:", status);
					}
				},
			);
		}
	};

	const getPinSize = (zoom) => {
		if (zoom < 6) return { width: 20, height: 20 };
		if (zoom < 9) return { width: 40, height: 40 };
		return { width: 56, height: 56 };
	};

	useEffect(() => {
		if (
			(searchQuery && searchQuery.trim() !== "") ||
			(viewMap && viewMap !== "none") ||
			data.searchWorkcenters
		)
			return;

		if (!centers?.length) return;

		const showAllCenters = () => {
			console.warn(
				"Geolocalización no disponible. Mostrando todos los centros en España.",
			);
			setMapCenter(defaultCenter);
			setUserLocation(null);
			setZoomLevel(5);
			setFilteredCenters(centers);
			setVisibleCenters(centers);
			setGeolocationFailed(true);
		};

		if (!navigator.geolocation) {
			showAllCenters();
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const coords = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				};
				setMapCenter(coords);
				setUserLocation(coords);
				setZoomLevel(9);
				setGeolocationFailed(false);
				filterCentersByDistance(coords, 10);
			},
			showAllCenters,
			{ enableHighAccuracy: true, timeout: 10000 },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (mapRef.current && filteredCenters.length > 0) {
			const bounds = new window.google.maps.LatLngBounds();

			filteredCenters.forEach((center) => {
				if (
					center.latitude &&
					center.longitude &&
					!isNaN(center.latitude) &&
					!isNaN(center.longitude)
				) {
					bounds.extend({
						lat: center.latitude,
						lng: center.longitude,
					});
				}
			});

			mapRef.current.fitBounds(bounds, { maxZoom: 14 });
			setVisibleCenters(filteredCenters);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filteredCenters]);

	useEffect(() => {
		if (
			mapRef.current &&
			selectedCenter?.latitude &&
			selectedCenter?.longitude
		) {
			mapRef.current.panTo({
				lat: selectedCenter.latitude,
				lng: selectedCenter.longitude,
			});
		}
	}, [mapRef, selectedCenter]);

	useEffect(() => {
		if (
			!infoCenter ||
			data.journey !== "B" ||
			infoCenter.temporarilyClosed
		) {
			setInfoCenterAppointments(null);
			return;
		}

		if (!bookingSent) {
			setInfoCenterAppointmentsLoading(false);
			setInfoCenterAppointments(null);
			return;
		}

		setInfoCenterAppointmentsLoading(true);
		setInfoCenterAppointments(null);

		(async () => {
			try {
				const appointments = await getAvailableAppointments(
					infoCenter.code,
					data.locator,
				);
				setInfoCenterAppointments(
					appointments.length > 0 ? appointments : null,
				);
			} catch (err) {
				console.error("Error loading center appointments:", err);
				setInfoCenterAppointments(null);
			} finally {
				setInfoCenterAppointmentsLoading(false);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [infoCenter, data.journey, data.locator, bookingSent]);

	if (!isLoaded) return <div>{t("Cargando mapa...")}</div>;

	return (
		<>
			<GoogleMap
				mapContainerStyle={containerStyle}
				center={mapCenter}
				zoom={zoomLevel}
				onLoad={(map) => {
					mapRef.current = map;

					const zoom = map.getZoom();

					if (typeof zoom === "number" && zoom > 0) {
						setZoomLevel(zoom);
					} else {
						console.warn("Zoom inválido al cargar el mapa:", zoom);
						map.setZoom(9);
						setZoomLevel(9);
					}

					setMapIsLoaded(true);
				}}
				onIdle={handleMapIdle}
				onZoomChanged={() => {
					if (!mapRef.current) return;

					const zoom = mapRef.current.getZoom();

					if (typeof zoom === "number" && zoom > 0) {
						setZoomLevel(zoom);
					} else {
						console.warn("Zoom inválido detectado:", zoom);
					}
				}}
				options={{
					mapTypeControl: false,
					fullscreenControl: false,
					streetViewControl: false,
					zoomControl: false,
					rotateControl: false,
					scaleControl: false,
					panControl: false,
					disableDefaultUI: true,
					styles: customMapStyle,
				}}
			>
				{visibleCenters.map((center) => {
					const { width, height } = getPinSize(zoomLevel);

					return (
						<Marker
							key={center.id}
							position={{
								lat: center.latitude,
								lng: center.longitude,
							}}
							onClick={() => handleMarkerClick(center)}
							icon={{
								url: center.temporarilyClosed
									? "resources/icons/pin_center_closed.svg"
									: selectedCenter?.id === center.id
										? "resources/icons/pin_selected_center.svg"
										: "resources/icons/pin_center.svg",
								scaledSize: new window.google.maps.Size(
									width,
									height,
								),
							}}
						/>
					);
				})}

				{userLocation && (
					<Marker
						position={userLocation}
						icon={{
							url: "resources/icons/map-dot.svg",
							scaledSize: new window.google.maps.Size(20, 20),
						}}
					/>
				)}
			</GoogleMap>

			<div
				className="map_button map_my_location"
				onPointerDown={() => {
					if (userLocation && mapRef.current) {
						mapRef.current.panTo(userLocation);
						mapRef.current.setZoom(12);

						setMapCenter(userLocation);
						filterCentersByDistance(userLocation, 100);
						setSearchQuery("");
					} else {
						if (navigator.geolocation) {
							navigator.geolocation.getCurrentPosition(
								(position) => {
									const coords = {
										lat: position.coords.latitude,
										lng: position.coords.longitude,
									};
									setMapCenter(coords);
									setUserLocation(coords);
									setZoomLevel(9);
									filterCentersByDistance(coords, 10);
									setSearchQuery("");
								},
								(error) => {
									if (error.code === 1) {
										alert(
											t(
												"Debes permitir el acceso a tu ubicación para usar esta funcionalidad.",
											),
										);
									} else if (error.code === 2) {
										alert(
											t(
												"No se pudo determinar tu ubicación. Verifica tu conexión o intenta de nuevo.",
											),
										);
									} else if (error.code === 3) {
										alert(
											t(
												"La solicitud de ubicación tardó demasiado. Intenta nuevamente.",
											),
										);
									} else {
										alert(
											t(
												"Ocurrió un error al obtener la ubicación.",
											),
										);
									}
									console.warn(
										"Error al obtener la ubicación:",
										error,
									);
								},
								{ enableHighAccuracy: true, timeout: 10000 },
							);
						} else {
							alert(
								t(
									"La geolocalización no es compatible con este navegador.",
								),
							);
						}
					}
				}}
			>
				<img src="resources/icons/my_location.svg" alt="Mi ubicación" />
			</div>

			{infoCenter && (
				<div
					className={
						"info_center" +
						(infoCenter.temporarilyClosed ? " closed" : "")
					}
				>
					<Icon
						name="close_arrow"
						className="center_icon"
						onClick={() => {
							setInfoCenter(null);
							setSelectedCenter(null);
						}}
					/>
					<h2 className="center_name">{infoCenter.name}</h2>
					{googleDetails && (
						<>
							<div className="center_address">
								<Icon name="location" />
								<p>
									{googleDetails.distance && (
										<strong>
											{googleDetails.distance}
										</strong>
									)}
									<span>{infoCenter.address}</span>
								</p>
							</div>
							{/* {googleDetails.rating && (
								<div className="center_rating">
									<Icon name="start" />
									<p>
										<strong>{googleDetails.rating}</strong>
										{googleDetails.totalReviews && (
											<span>
												{googleDetails.totalReviews}{" "}
												{t("reseñas")}
											</span>
										)}
									</p>
								</div>
							)} */}
						</>
					)}
					{infoCenter.temporarilyClosed ? (
						<p className="schedules closed">
							{t("Cerrado hasta el")}{" "}
							{new Date(
								infoCenter.closureEndDate,
							).toLocaleDateString("es-ES", {
								day: "numeric",
								month: "long",
								year: "numeric",
							})}
						</p>
					) : (
						<p className="schedules">
							{t("Lunes a viernes:")} {infoCenter.morningFrom} -{" "}
							{infoCenter.morningTo} {t("y")}{" "}
							{infoCenter.eveningFrom} - {infoCenter.eveningTo}
							{showSaturday && (
								<>
									<br />
									{t("Sábado:")}{" "}
									{infoCenter.saturdayFrom ===
									infoCenter.saturdayTo
										? infoCenter.saturdayFrom
										: `${infoCenter.saturdayFrom} - ${infoCenter.saturdayTo}`}
								</>
							)}
						</p>
					)}
					{data.journey === "A" &&
						!infoCenter.temporarilyClosed &&
						!viewMap && (
							<div className="info_status">
								{infoCenter.availability === "high" && (
									<span className="info_badge highAvailabilityInfo">
										<Icon name="calendar" />
										Alta disponibilidad
									</span>
								)}
								{/* {infoCenter.availability === "medium" && (
									<span className="info_badge lastAppointments">
										<Icon name="appointment" />
										Últimas citas
									</span>
								)} */}
								<button
									className="center_cta"
									onPointerDown={(e) => {
										e.stopPropagation();
										selectCenter(infoCenter)();
									}}
								>
									Seleccionar taller
									<Icon name="arrow" />
								</button>
							</div>
						)}
					{data.journey === "B" &&
						!infoCenter.temporarilyClosed &&
						!viewMap && (
							<AvailableAppointments
								appointments={infoCenterAppointments}
								loading={infoCenterAppointmentsLoading}
								centerCode={infoCenter.code}
								selectCenter={selectCenter}
								infoCenter={infoCenter}
								viewMap={viewMap}
								onViewMore={(code) => {
								setData((prev) => ({
									...prev,
									centerId: infoCenter.id,
									centerName: infoCenter.name,
									centerCode: code,
									centerAddress: infoCenter.address,
								}));
									navigate(routes.selectDateTime);
								}}
								onSelectAppointment={(apt, code) => {
								const timeData = extractTimeData(apt)
							const changes = {
								centerId: infoCenter.id,
								centerName: infoCenter.name,
								centerCode: code,
								centerAddress: infoCenter.address,
								date: apt.fullDate,
								...timeData,
							};
								setData((prev) => ({ ...prev, ...changes }));
								if (data.locator) {
										const apiUrl =
											process.env.REACT_APP_API_URL ||
											"http://localhost:3000";
										const token =
											process.env.REACT_APP_API_TOKEN;
										fetch(
											`${apiUrl}/bookings/${data.locator}`,
											{
												method: "POST",
												headers: {
													"Content-Type":
														"application/json",
												},
											body: JSON.stringify({
												...data,
												...changes,
												token,
											}),
											},
										).catch((err) => console.error(err));
									}
									navigate(routes.bookingEnd);
								}}
							/>
						)}
					{(data.journey === null || !data.journey) &&
						!infoCenter.temporarilyClosed &&
						!viewMap && (
							<button
								className="center_cta"
								onPointerDown={(e) => {
									e.stopPropagation();
									selectCenter(infoCenter)();
								}}
							>
								Seleccionar taller
								<Icon name="arrow" />
							</button>
						)}
					{viewMap != null && (
						<div className="center_links">
							<a
								href={
									`${RALARSA_URL}/es/solicita-cita-con-taller-de-lunas?centerCode=` +
									infoCenter.code
								}
								title={infoCenter.name}
								target="_top"
								rel="noopener noreferrer"
								className="center_booking"
							>
								{t("Cita rápida")}{" "}
							</a>
							<a
								href={
									`${RALARSA_URL}/es/taller-lunas-coche/` +
									infoCenter.slug
								}
								title={infoCenter.name}
								target="_top"
								rel="noopener noreferrer"
								className="info_btn"
							>
								{t("Ver taller")} <Icon name="arrow" />
							</a>
						</div>
					)}
				</div>
			)}
		</>
	);
};
